import {isAutomatedReviewBot, reviewerDisplayName} from '../ReviewerIdentity'
import {DeferredType} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import type {
  CompletedReview,
  CopilotReviewerRow,
  PendingReviewRequest,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import {isCopilotReviewer} from '../copilot-reviewer'
import {CompletedReviewRow} from './CompletedReviewRow'
import {CopilotRequestRow} from './CopilotRequestRow'
import {DeferredCopilotReviewRow} from './DeferredCopilotReviewRow'
import {PendingReviewRow} from './PendingReviewRow'

export function CopilotRow({
  copilotPendingRequest,
  copilotCompletedReview,
  copilotReviewerRow,
  awaitingInitialCopilotRequest,
  onClearMutationError,
  onCopilotRequestMutationError,
  onReviewerMutationError,
  onReviewerMutationSuccess,
  onRestoreFocus,
  onMutationSuccess,
}: {
  copilotPendingRequest: PendingReviewRequest | undefined
  copilotCompletedReview: CompletedReview | undefined
  copilotReviewerRow: CopilotReviewerRow | undefined
  awaitingInitialCopilotRequest: boolean
  onClearMutationError: () => void
  onCopilotRequestMutationError: (origin: Element | null) => void
  onReviewerMutationError: (reviewerName: string | undefined) => (origin: Element | null) => void
  onReviewerMutationSuccess: (reviewerName: string | undefined) => () => void
  onRestoreFocus: () => void
  onMutationSuccess: (message: string) => void
}) {
  if (copilotPendingRequest && copilotReviewerRow) {
    const reviewerName = copilotPendingRequest.reviewer
      ? reviewerDisplayName(copilotPendingRequest.reviewer)
      : 'Copilot'
    if (copilotPendingRequest.deferredType === DeferredType.COPILOT) {
      return (
        <DeferredCopilotReviewRow
          request={copilotPendingRequest}
          row={copilotReviewerRow}
          onMutationStart={onClearMutationError}
          onMutationError={onCopilotRequestMutationError}
          onMutationSuccess={() => onMutationSuccess(`Review requested from ${reviewerName}`)}
          onCloseWithoutTarget={onRestoreFocus}
        />
      )
    }
    return (
      <PendingReviewRow
        request={copilotPendingRequest}
        copilotReviewerRow={copilotReviewerRow}
        lastCompletedCopilotReviewEffort={copilotReviewerRow.lastExecutedReviewEffort}
      />
    )
  }
  if (copilotCompletedReview) {
    const reviewerName = copilotCompletedReview.reviewer
      ? reviewerDisplayName(copilotCompletedReview.reviewer)
      : 'Copilot'
    return (
      <CompletedReviewRow
        review={copilotCompletedReview}
        muted={
          isCopilotReviewer(copilotCompletedReview.reviewer) || isAutomatedReviewBot(copilotCompletedReview.reviewer)
        }
        copilotReviewerRow={copilotReviewerRow}
        onMutationStart={onClearMutationError}
        onMutationError={onReviewerMutationError(reviewerName)}
        onMutationSuccess={onReviewerMutationSuccess(reviewerName)}
        onCloseWithoutTarget={onRestoreFocus}
      />
    )
  }
  if (awaitingInitialCopilotRequest && copilotReviewerRow) {
    return (
      <CopilotRequestRow
        row={copilotReviewerRow}
        onMutationStart={onClearMutationError}
        onMutationError={onCopilotRequestMutationError}
        onMutationSuccess={() =>
          onMutationSuccess(
            `Review requested from ${copilotReviewerRow.reviewer ? reviewerDisplayName(copilotReviewerRow.reviewer) : 'Copilot'}`,
          )
        }
        onCloseWithoutTarget={onRestoreFocus}
      />
    )
  }
  return null
}
